import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader } from './ui/card';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EiraaProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const response = await axios.get(`${API}/products?active_only=true`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <section id="products" className="py-24 bg-gradient-to-b from-slate-50 to-white">
        <div className="container mx-auto px-4 text-center">
          <div className="text-gray-600">Loading products...</div>
        </div>
      </section>
    );
  }

  return (
    <section id="products" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Our Collection</span>
          <h2 className="text-4xl md:text-5xl font-light text-slate-900 mt-3 mb-4">
            EIRAA Sauna Series by FASTFIX
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Discover our complete range of premium sauna solutions, from economic to bespoke luxury options.
          </p>
        </div>

        {products.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 mb-4">No products available at the moment.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 max-w-7xl mx-auto">
            {products.map((product, index) => (
            <div
              key={product.id}
              className="group relative"
              style={{
                animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
              }}
            >
              <Card className="h-full border-0 overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                {/* Image Section */}
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent"></div>
                  
                  {/* Product Name Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-2xl font-bold text-white mb-2 drop-shadow-lg">
                      {product.name}
                    </h3>
                    {product.category && (
                      <p className="text-amber-300 text-sm font-medium">
                        {product.category} Collection
                      </p>
                    )}
                  </div>
                </div>
                
                {/* Content Section */}
                <div className="p-6">
                  <p className="text-gray-600 text-sm leading-relaxed mb-6 min-h-[60px]">
                    {product.description}
                  </p>

                  {/* Features List */}
                  <div className="space-y-3 mb-6">
                    {product.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-3">
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-600 mt-2 flex-shrink-0"></div>
                        <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Category */}
                  {product.category && (
                    <div className="mb-6 pb-6 border-b border-gray-100">
                      <p className="text-xs text-gray-500 mb-2">Category:</p>
                      <p className="text-sm font-medium text-gray-900">
                        {product.category}
                      </p>
                    </div>
                  )}

                  {/* CTA Button */}
                  <Button
                    onClick={scrollToContact}
                    className="w-full bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600 text-white font-semibold py-6 rounded-xl transition-all duration-300 group shadow-md hover:shadow-xl"
                  >
                    Request Quote
                    <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                  </Button>
                </div>
              </Card>
            </div>
          ))}
        </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-6">
            Can&apos;t find exactly what you&apos;re looking for? We offer fully customizable solutions.
          </p>
          <Button
            onClick={scrollToContact}
            size="lg"
            className="bg-slate-900 hover:bg-slate-800 text-white px-8 py-6"
          >
            Contact Us for Custom Solutions
          </Button>
        </div>
      </div>
    </section>
  );
};

export default EiraaProducts;